'use client'

import Link from 'next/link'
import { useState } from 'react'

import LegalBackButton from '../LegalBackButton'

type Locale = 'pt' | 'en'

const updatedAt = 'May 11, 2026'

const copy = {
  en: {
    brand: '7Songs / 7Corp',
    title: 'Privacy Policy',
    updated: 'Last updated',
    sections: [
      {
        title: 'YouTube API Services',
        paragraphs: [
          '7Songs uses YouTube API Services to let authenticated users connect a YouTube account, read channel and playlist information, search YouTube videos, create playlists, add videos to playlists, and display YouTube-related metadata in the product.',
          <>
            Google&apos;s privacy practices also apply to Google and YouTube services. Please review the{' '}
            <a className="text-cyan-300 underline" href="https://www.google.com/policies/privacy" target="_blank" rel="noopener noreferrer">
              Google Privacy Policy
            </a>.
          </>,
        ],
      },
      {
        title: 'Information We Access, Collect, and Store',
        paragraphs: [
          'When you authorize YouTube access, we may access and process OAuth tokens, channel ID, channel name, channel thumbnail, playlist IDs, playlist titles, playlist descriptions, playlist item counts, video IDs, video titles, thumbnails, channel titles, and other metadata needed to complete the user-requested workflow.',
          'We use cookies, local storage, and similar technologies to keep you signed in, remember selected YouTube profiles, store short-lived access state, and complete OAuth flows. Google/YouTube and operational service providers may also place, access, or recognize cookies or similar technologies when authentication flows, YouTube content, or related services are loaded.',
        ],
      },
      {
        title: 'How We Use and Share Information',
        paragraphs: [
          'We use YouTube API data to provide playlist management, video search, playlist creation, playlist item insertion, display, troubleshooting, security, and support. We do not sell YouTube API data.',
          'Information may be processed by internal team members and infrastructure providers that help us operate the service. YouTube content and embedded experiences are provided by Google/YouTube, and your browser may communicate with Google/YouTube when those features are loaded.',
          'Third parties, including Google/YouTube, may serve content through YouTube-related features, including advertisements where applicable.',
        ],
      },
      {
        title: 'Deleting Data and Revoking Access',
        paragraphs: [
          'You can disconnect YouTube inside the product where available, request deletion by email, or revoke 7Songs access directly from Google security settings:',
          <a className="text-cyan-300 underline break-words" href="https://myaccount.google.com/connections?filters=3,4&hl=en" target="_blank" rel="noopener noreferrer">
            https://myaccount.google.com/connections?filters=3,4&hl=en
          </a>,
          <>
            For deletion requests or privacy questions, contact{' '}
            <a className="text-cyan-300 underline" href="mailto:renan@7sete.com">renan@7sete.com</a>.
          </>,
        ],
      },
      {
        title: 'Contact Information',
        paragraphs: [
          <>
            7Corp - 7SETE LTDA, PRAC THIRSO TARCISIO GIVISIEZ 57 APT 4, Givisiez,
            CEP 36820-000, Divino-MG, Brazil. Email:{' '}
            <a className="text-cyan-300 underline" href="mailto:renan@7sete.com">renan@7sete.com</a>.
          </>,
        ],
      },
    ],
    terms: 'Terms of Service',
    contact: 'Contact',
  },
  pt: {
    brand: '7Songs / 7Corp',
    title: 'Politica de Privacidade',
    updated: 'Ultima atualizacao',
    sections: [
      {
        title: 'YouTube API Services',
        paragraphs: [
          'A 7Songs usa os YouTube API Services para permitir que usuarios autenticados conectem uma conta YouTube, leiam informacoes de canal e playlists, pesquisem videos, criem playlists, adicionem videos a playlists e exibam metadados relacionados ao YouTube no produto.',
          <>
            As praticas de privacidade do Google tambem se aplicam aos servicos Google e YouTube. Consulte a{' '}
            <a className="text-cyan-300 underline" href="https://www.google.com/policies/privacy" target="_blank" rel="noopener noreferrer">
              Politica de Privacidade do Google
            </a>.
          </>,
        ],
      },
      {
        title: 'Informacoes Que Acessamos, Coletamos e Armazenamos',
        paragraphs: [
          'Quando voce autoriza o acesso ao YouTube, podemos acessar e processar tokens OAuth, ID do canal, nome do canal, thumbnail do canal, IDs de playlists, titulos de playlists, descricoes, contagem de itens, IDs de videos, titulos de videos, thumbnails, nomes de canais e outros metadados necessarios para concluir o fluxo solicitado pelo usuario.',
          'Usamos cookies, local storage e tecnologias similares para manter voce autenticado, lembrar perfis YouTube selecionados, armazenar estado temporario de acesso e concluir fluxos OAuth. Google/YouTube e provedores operacionais tambem podem inserir, acessar ou reconhecer cookies ou tecnologias similares quando fluxos de autenticacao, conteudo do YouTube ou servicos relacionados sao carregados.',
        ],
      },
      {
        title: 'Como Usamos e Compartilhamos Informacoes',
        paragraphs: [
          'Usamos dados da YouTube API para gerenciamento de playlists, busca de videos, criacao de playlists, insercao de itens, exibicao, suporte, seguranca e resolucao de problemas. Nao vendemos dados da YouTube API.',
          'As informacoes podem ser processadas por membros internos da equipe e provedores de infraestrutura que ajudam a operar o servico. Conteudos e experiencias do YouTube sao fornecidos por Google/YouTube, e seu navegador pode se comunicar com Google/YouTube quando esses recursos sao carregados.',
          'Terceiros, incluindo Google/YouTube, podem veicular conteudo por meio de recursos relacionados ao YouTube, incluindo anuncios quando aplicavel.',
        ],
      },
      {
        title: 'Exclusao de Dados e Revogacao de Acesso',
        paragraphs: [
          'Voce pode desconectar o YouTube dentro do produto quando disponivel, solicitar exclusao por email ou revogar o acesso da 7Songs diretamente nas configuracoes de seguranca do Google:',
          <a className="text-cyan-300 underline break-words" href="https://myaccount.google.com/connections?filters=3,4&hl=en" target="_blank" rel="noopener noreferrer">
            https://myaccount.google.com/connections?filters=3,4&hl=en
          </a>,
          <>
            Para pedidos de exclusao ou duvidas de privacidade, entre em contato pelo email{' '}
            <a className="text-cyan-300 underline" href="mailto:renan@7sete.com">renan@7sete.com</a>.
          </>,
        ],
      },
      {
        title: 'Informacoes de Contato',
        paragraphs: [
          <>
            7Corp - 7SETE LTDA, PRAC THIRSO TARCISIO GIVISIEZ 57 APT 4, Givisiez,
            CEP 36820-000, Divino-MG, Brasil. Email:{' '}
            <a className="text-cyan-300 underline" href="mailto:renan@7sete.com">renan@7sete.com</a>.
          </>,
        ],
      },
    ],
    terms: 'Termos de Servico',
    contact: 'Contato',
  },
} satisfies Record<Locale, {
  brand: string
  title: string
  updated: string
  sections: Array<{ title: string; paragraphs: React.ReactNode[] }>
  terms: string
  contact: string
}>

export default function PrivacyPage() {
  const [locale, setLocale] = useState<Locale>('en')
  const page = copy[locale]

  return (
    <main className="min-h-screen bg-[#09080b] text-white px-6 py-12">
      <div className="mx-auto max-w-3xl space-y-8">
        <header className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <LegalBackButton />
            <div className="flex items-center gap-1 rounded-lg border border-white/15 bg-black/20 p-1">
              {(['pt', 'en'] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setLocale(option)}
                  className={`rounded px-3 py-1.5 text-xs font-bold transition-colors ${
                    locale === option
                      ? 'bg-cyan-500/25 text-cyan-200'
                      : 'text-white/60 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  {option.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <p className="text-sm font-semibold text-cyan-300">{page.brand}</p>
          <h1 className="text-3xl font-black">{page.title}</h1>
          <p className="text-sm text-neutral-400">{page.updated}: {updatedAt}</p>
        </header>

        {page.sections.map((section) => (
          <section key={section.title} className="space-y-3 text-sm leading-6 text-neutral-300">
            <h2 className="text-xl font-bold text-white">{section.title}</h2>
            {section.paragraphs.map((paragraph, index) => (
              <p key={`${section.title}-${index}`}>{paragraph}</p>
            ))}
          </section>
        ))}

        <nav className="flex flex-wrap gap-3 pt-4 text-sm">
          <Link className="text-cyan-300 underline" href="/terms">{page.terms}</Link>
          <Link className="text-cyan-300 underline" href="/contact">{page.contact}</Link>
        </nav>
      </div>
    </main>
  )
}
